import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AdminUserDto,
  AdminValuationStateDto,
  adminApi,
  isUnauthorized,
} from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminValuationScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

type SortKey = 'updatedAtUtc' | 'userId' | 'username' | 'stockSymbol' | 'id';

const AdminValuationScreen: React.FC<AdminValuationScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);
  const colors = useMemo(() => ({
    card: theme.cardBg,
    border: theme.border,
    text: theme.text,
    subText: theme.subText,
    hover: theme.hoverBg,
    inputBg: theme.inputBg,
    primary: theme.primary,
    danger: theme.danger,
    radius: theme.radius,
  }), [theme]);

  const [items, setItems] = useState<AdminValuationStateDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [gridSearch, setGridSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'updatedAtUtc', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [criteriaDraft, setCriteriaDraft] = useState({ username: '', stockSymbol: '' });
  const [criteriaApplied, setCriteriaApplied] = useState(criteriaDraft);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState<AdminValuationStateDto | null>(null);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };
    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    return base;
  };

  const normalize = (v: unknown): string => (v ?? '').toString().trim().toLowerCase();

  const userLookup = useMemo(() => {
    const map = new Map<number, string>();
    (users ?? []).forEach((u) => map.set(u.id, u.username));
    return map;
  }, [users]);

  const refresh = useCallback(async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const [states, usersList] = await Promise.all([
        adminApi.listValuationStates(token),
        adminApi.listUsers(token),
      ]);
      setItems(states);
      setUsers(usersList);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) { onUnauthorized(); return; }
      setError(e instanceof Error ? e.message : 'Failed loading valuation states');
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized, token]);

  useEffect(() => { void refresh({ clearSelection: true }); }, [refresh]);

  const applyCriteria = () => { setCriteriaApplied(criteriaDraft); setPage(1); setSelectedIds(new Set()); };
  const clearCriteria = () => {
    const cleared = { username: '', stockSymbol: '' };
    setCriteriaDraft(cleared); setCriteriaApplied(cleared); setGridSearch(''); setPage(1); setSelectedIds(new Set());
  };

  const toggleSort = (key: SortKey) => setSort((prev) => prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' });
  const sortIndicator = (key: SortKey) => sort.key !== key ? '' : sort.dir === 'asc' ? ' ▲' : ' ▼';

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const q = normalize(gridSearch);
    const usernameFilter = criteriaApplied.username.trim();
    const stockFilter = normalize(criteriaApplied.stockSymbol);

    const result = list.filter((x) => {
      if (usernameFilter.length > 0) {
        const un = (userLookup.get(x.userId) ?? x.username ?? '').toString();
        if (!normalize(un).includes(normalize(usernameFilter))) return false;
      }
      if (stockFilter.length > 0 && !normalize(x.stockSymbol).includes(stockFilter)) return false;
      if (q.length > 0) {
        const hay = [x.id, x.userId, userLookup.get(x.userId) ?? x.username, x.stockSymbol, x.updatedAtUtc]
          .map((v) => (v ?? '').toString().toLowerCase()).join(' | ');
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    result.sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      const val = (x: AdminValuationStateDto): string | number => {
        if (sort.key === 'username') return (userLookup.get(x.userId) ?? x.username ?? '').toString().toLowerCase();
        if (sort.key === 'updatedAtUtc') { const d = new Date((x.updatedAtUtc ?? '') as string); return isNaN(d.getTime()) ? 0 : d.getTime(); }
        const v = (x as any)[sort.key];
        return typeof v === 'number' ? v : (v ?? '').toString().toLowerCase();
      };
      const av = val(a); const bv = val(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return av.toString().localeCompare(bv.toString()) * dir;
    });

    return result;
  }, [criteriaApplied, gridSearch, items, sort, userLookup]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? []).map((u) => u.username.trim()).filter(Boolean);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const total = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);

  useEffect(() => { if (page !== pageSafe) setPage(pageSafe); }, [page, pageSafe]);

  const pageItems = useMemo(() => {
    const start = (pageSafe - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, pageSafe, pageSize]);

  const toggleAllOnPage = () => setSelectedIds((prev) => {
    const next = new Set(prev);
    const allSelected = pageItems.every((x) => next.has(x.id));
    pageItems.forEach((x) => { allSelected ? next.delete(x.id) : next.add(x.id); });
    return next;
  });

  const toggleOne = (id: number) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} item(s)?`)) return;
    setLoading(true);
    setError(null);
    try {
      await adminApi.deleteValuationStates(token, { ids: Array.from(selectedIds) });
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) { onUnauthorized(); return; }
      setError(e instanceof Error ? e.message : 'Failed deleting valuation states');
      setLoading(false);
    }
  };

  const cellStyle: React.CSSProperties = { padding: 10, color: colors.text, fontWeight: 800, fontSize: 13 };
  const thStyle: React.CSSProperties = { textAlign: 'left', padding: 10, cursor: 'pointer', color: colors.text, fontWeight: 900, fontSize: 13 };

  const fmtField = (v: string) => v.trim().length > 0 ? v : '—';

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Filter panel */}
      <div style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}`, borderRadius: colors.radius, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 900, color: colors.text }}>Valuation</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={buttonStyle('neutral', loading)} onClick={() => void refresh({ clearSelection: true })}>Refresh</button>
            <button style={buttonStyle('danger', loading || selectedIds.size === 0)} disabled={loading || selectedIds.size === 0} onClick={() => void deleteSelected()}>
              Delete Selected
            </button>
          </div>
        </div>

        {error ? <div style={{ marginTop: 10, color: colors.danger, fontWeight: 800, fontSize: 13 }}>{error}</div> : null}

        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
            <input value={criteriaDraft.username} onChange={(e) => setCriteriaDraft((p) => ({ ...p, username: e.target.value }))}
              placeholder="Select or type..." list="admin-valuation-username-options" style={inputStyle} />
            <datalist id="admin-valuation-username-options">
              {usernameOptions.map((u) => <option key={u} value={u} />)}
            </datalist>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Stock Symbol</div>
            <input value={criteriaDraft.stockSymbol} onChange={(e) => setCriteriaDraft((p) => ({ ...p, stockSymbol: e.target.value }))}
              placeholder="e.g. AAPL" style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Search</div>
            <input value={gridSearch} onChange={(e) => setGridSearch(e.target.value)} placeholder="Search" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={buttonStyle('primary', loading)} disabled={loading} onClick={applyCriteria}>Apply</button>
          <button style={buttonStyle('neutral', loading)} disabled={loading} onClick={clearCriteria}>Clear</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ color: colors.subText, fontWeight: 800, fontSize: 13 }}>Page size</div>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value) || 10); setPage(1); }}
              style={{ ...inputStyle, width: 90, padding: '0 8px' }}>
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}`, borderRadius: colors.radius, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: colors.card, borderBottom: `1px solid ${colors.border}` }}>
                <th style={{ textAlign: 'left', padding: 10, width: 42 }}>
                  <input type="checkbox" checked={pageItems.length > 0 && pageItems.every((x) => selectedIds.has(x.id))} onChange={toggleAllOnPage} />
                </th>
                {(['id', 'userId', 'username', 'stockSymbol'] as SortKey[]).map((k) => (
                  <th key={k} onClick={() => toggleSort(k)} style={thStyle}>
                    {k === 'stockSymbol' ? 'Stock' : k === 'userId' ? 'User Id' : k === 'username' ? 'Username' : 'Id'}
                    {sortIndicator(k)}
                  </th>
                ))}
                <th style={{ ...thStyle, cursor: 'default' }}>EPS Base</th>
                <th style={{ ...thStyle, cursor: 'default' }}>EPS Growth%</th>
                <th style={{ ...thStyle, cursor: 'default' }}>EPS P/E</th>
                <th style={{ ...thStyle, cursor: 'default' }}>OCF Base</th>
                <th style={{ ...thStyle, cursor: 'default' }}>OCF Growth%</th>
                <th style={{ ...thStyle, cursor: 'default' }}>OCF P/OCF</th>
                <th onClick={() => toggleSort('updatedAtUtc')} style={thStyle}>Updated{sortIndicator('updatedAtUtc')}</th>
                <th style={{ textAlign: 'right', padding: 10, width: 100, color: colors.text, fontWeight: 900, fontSize: 13 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={13} style={{ padding: 14, color: colors.subText, fontWeight: 800 }}>Loading…</td></tr>
              ) : pageItems.length === 0 ? (
                <tr><td colSpan={13} style={{ padding: 14, color: colors.subText, fontWeight: 800 }}>No items</td></tr>
              ) : (
                pageItems.map((x) => (
                  <tr key={x.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={{ padding: 10 }}><input type="checkbox" checked={selectedIds.has(x.id)} onChange={() => toggleOne(x.id)} /></td>
                    <td style={cellStyle}>{x.id}</td>
                    <td style={cellStyle}>{x.userId}</td>
                    <td style={cellStyle}>{userLookup.get(x.userId) ?? x.username ?? '—'}</td>
                    <td style={cellStyle}>{x.stockSymbol}</td>
                    <td style={cellStyle}>{fmtField(x.epsBase)}</td>
                    <td style={cellStyle}>{fmtField(x.epsGrowthRate)}</td>
                    <td style={cellStyle}>{fmtField(x.epsMultiple)}</td>
                    <td style={cellStyle}>{fmtField(x.cashFlowBase)}</td>
                    <td style={cellStyle}>{fmtField(x.cashFlowGrowthRate)}</td>
                    <td style={cellStyle}>{fmtField(x.cashFlowMultiple)}</td>
                    <td style={{ ...cellStyle, color: colors.subText }}>{x.updatedAtUtc ? new Date(x.updatedAtUtc).toLocaleString() : '—'}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>
                      <button style={buttonStyle('neutral', loading)} disabled={loading} onClick={() => { setPreviewRow(x); setPreviewOpen(true); }}>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, borderTop: `1px solid ${colors.border}`, color: colors.subText, fontWeight: 800, fontSize: 13 }}>
          <div>{total === 0 ? '0' : (pageSafe - 1) * pageSize + 1}–{Math.min(pageSafe * pageSize, total)} of {total}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={buttonStyle('neutral', pageSafe <= 1)} disabled={pageSafe <= 1} onClick={() => setPage(1)}>First</button>
            <button style={buttonStyle('neutral', pageSafe <= 1)} disabled={pageSafe <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
            <div style={{ color: colors.text }}>Page {pageSafe} / {totalPages}</div>
            <button style={buttonStyle('neutral', pageSafe >= totalPages)} disabled={pageSafe >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            <button style={buttonStyle('neutral', pageSafe >= totalPages)} disabled={pageSafe >= totalPages} onClick={() => setPage(totalPages)}>Last</button>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      {previewOpen && previewRow ? (
        <div
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setPreviewOpen(false); setPreviewRow(null); } }}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}`, borderRadius: colors.radius, padding: 24, width: 600, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontWeight: 900, color: colors.text, fontSize: 16 }}>
                Valuation — {previewRow.stockSymbol} ({userLookup.get(previewRow.userId) ?? previewRow.username ?? `User ${previewRow.userId}`})
              </div>
              <button style={buttonStyle('neutral')} onClick={() => { setPreviewOpen(false); setPreviewRow(null); }}>Close</button>
            </div>

            {(['Earnings', 'Cash Flow'] as const).map((section) => {
              const isEps = section === 'Earnings';
              return (
                <div key={section} style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 900, color: colors.text, fontSize: 14, marginBottom: 10, borderBottom: `1px solid ${colors.border}`, paddingBottom: 6 }}>{section}</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {[
                        ['Base ($)', isEps ? previewRow.epsBase : previewRow.cashFlowBase],
                        ['Growth Rate (%)', isEps ? previewRow.epsGrowthRate : previewRow.cashFlowGrowthRate],
                        ['Multiple', isEps ? previewRow.epsMultiple : previewRow.cashFlowMultiple],
                        ['Years', isEps ? previewRow.epsYears : previewRow.cashFlowYears],
                        ['Min. Return (%)', isEps ? previewRow.epsMinimumReturn : previewRow.cashFlowMinimumReturn],
                      ].map(([label, value]) => (
                        <tr key={label} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={{ padding: '7px 10px', fontWeight: 800, color: colors.subText, width: '50%' }}>{label}</td>
                          <td style={{ padding: '7px 10px', fontWeight: 700, color: colors.text }}>{fmtField(value as string)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}

            <div style={{ color: colors.subText, fontSize: 12, fontWeight: 700, marginTop: 8 }}>
              Updated: {previewRow.updatedAtUtc ? new Date(previewRow.updatedAtUtc).toLocaleString() : '—'}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminValuationScreen;
